'use client'

import React from 'react'
import { FilterSelect } from './FilterSelect'

export interface SegmentedTab {
  id: string
  label: string
  badge?: string
}

export interface SegmentedTabsProps {
  tabs: SegmentedTab[]
  value: string
  onChange: (id: string) => void
  ariaLabel: string
  /**
   * Below this width the tabs become a native <select>. Default `sm`, which is
   * right for two or three tabs; pass 'md' for a five-tab set that would still
   * be unreadable at 640px.
   */
  collapseBelow?: 'sm' | 'md' | 'lg'
  className?: string
}

const HIDE = { sm: 'hidden sm:flex', md: 'hidden md:flex', lg: 'hidden lg:flex' }
const SHOW = { sm: 'sm:hidden', md: 'md:hidden', lg: 'lg:hidden' }

/**
 * A segmented control for switching between views of the same screen.
 *
 * Below the collapse point it becomes a real <select> instead of squeezing five
 * labels into 375px. This is the same rule the filters follow, applied to tabs:
 * on a phone, picking one of a known set of options is the platform's job.
 * Chosen by CSS, so no hydration flash.
 */
export const SegmentedTabs: React.FC<SegmentedTabsProps> = ({
  tabs,
  value,
  onChange,
  ariaLabel,
  collapseBelow = 'sm',
  className = '',
}) => (
  <>
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`${HIDE[collapseBelow]} gap-px p-0.5 bg-white rounded-lg w-full ${className}`}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === value
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={`flex-1 px-2 sm:px-4 py-2.5 rounded-md text-center transition-colors text-[13px] sm:text-sm font-poppins ${
              isActive
                ? 'bg-dashboard-accent-soft text-dashboard-text font-semibold'
                : 'bg-white text-dashboard-muted font-medium hover:bg-gray-50'
            }`}
          >
            {tab.label}
            {tab.badge && (
              <span className="ms-1.5 px-1.5 py-[1px] rounded-full bg-dashboard-positive-bg text-dashboard-positive-fg text-[10px] font-semibold align-middle">
                {tab.badge}
              </span>
            )}
          </button>
        )
      })}
    </div>

    <FilterSelect
      ariaLabel={ariaLabel}
      options={tabs.map((t) => ({ value: t.id, label: t.label }))}
      value={value}
      onChange={onChange}
      className={`${SHOW[collapseBelow]} w-full`}
    />
  </>
)
